/**
 * Per-scenario "trade dossier" manifest — surfaced at the end of each
 * scenario (T1Reveal / T2Reveal / T3Reveal) as the "quick access and
 * sharing" folder. Each entry resolves to a list of viewable docs and
 * a one-line trade summary for the dossier header.
 */

export type DossierDoc = {
  key:        string;
  label:      string;
  pdfPath:    string;
  issuerName: string;
  issuerOrg:  string;
};

export type Dossier = {
  tradeRef: string;
  parties:  string;
  summary:  string;
  docs:     DossierDoc[];
};

// Shared poultry-cert manifest reused by T1 + T2.
const POULTRY_CERTS: DossierDoc[] = [
  {
    key:        "halal",
    label:      "Halal Slaughter Cert.",
    pdfPath:    "/sign-docs/halal-certificate.pdf",
    issuerName: "CDIAL Halal",
    issuerOrg:  "Centro de Divulgação do Islam · BR",
  },
  {
    key:        "vet",
    label:      "Veterinary Health Cert.",
    pdfPath:    "/sign-docs/vet-certificate.pdf",
    issuerName: "MAPA",
    issuerOrg:  "Ministério da Agricultura, Pecuária e Abastecimento · BR",
  },
  {
    key:        "coo",
    label:      "Certificate of Origin",
    pdfPath:    "/sign-docs/certificate-of-origin.pdf",
    issuerName: "ABCC",
    issuerOrg:  "Arab-Brazilian Chamber of Commerce · BR",
  },
  {
    key:        "ebl",
    label:      "Electronic Bill of Lading",
    pdfPath:    "/sign-docs/ebl.pdf",
    issuerName: "Demo Liner Co.",
    issuerOrg:  "Carrier (electronic original on Orbitra ledger)",
  },
];

const T1: Dossier = {
  tradeRef: "ORB-CTR-2026-0042",
  parties:  "Aurora Aves Brasil S.A. ↔ Al Madar Foods LLC",
  summary:  "CIF Jebel Ali · USD 850K · 28 t frozen halal chicken",
  docs: [
    {
      key:        "contract",
      label:      "Sale & Purchase Agreement",
      pdfPath:    "/sign-docs/contract.pdf",
      issuerName: "Aurora Aves Brasil S.A. & Al Madar Foods LLC",
      issuerOrg:  "Bilateral · signed by both Corp DIDs",
    },
    {
      key:        "einvoice",
      label:      "Commercial E-Invoice",
      pdfPath:    "/sign-docs/einvoice.pdf",
      issuerName: "Aurora Aves Brasil S.A.",
      issuerOrg:  "Seller (Issuer) · BR · PINT AE",
    },
    {
      key:        "insurance",
      label:      "Marine Cargo Insurance",
      pdfPath:    "/sign-docs/insurance.pdf",
      issuerName: "Aurora Aves Brasil S.A.",
      issuerOrg:  "Insured (Seller arranges under CIF) · BR",
    },
    ...POULTRY_CERTS,
  ],
};

const T2: Dossier = {
  tradeRef: "ORB-CTR-2026-0187",
  parties:  "Aurora Aves Brasil S.A. ↔ Al Madar Foods LLC",
  summary:  "CIF Jebel Ali · USD 1.9M · 65 t · FAB-financed repeat",
  docs: T1.docs.map((d) => ({ ...d })), // same paperwork, repeat trade
};

const T3: Dossier = {
  tradeRef: "ORB-CTR-2026-0342",
  parties:  "Gulf Phosphate UAE FZE ↔ Pampagro Importação Ltda",
  summary:  "FOB Abu Dhabi Port → Itaqui · USD 3.4M · 8,000 t fertiliser (urea, granular)",
  docs: [
    {
      key:        "contract",
      label:      "Sale & Purchase Agreement",
      pdfPath:    "/sign-docs/contract-t3.pdf",
      issuerName: "Gulf Phosphate UAE FZE & Pampagro Importação Ltda",
      issuerOrg:  "Bilateral · signed by both Corp DIDs",
    },
    {
      key:        "einvoice",
      label:      "Commercial E-Invoice",
      pdfPath:    "/sign-docs/einvoice-t3.pdf",
      issuerName: "Gulf Phosphate UAE FZE",
      issuerOrg:  "Seller (Issuer) · UAE · UAE FTA",
    },
    {
      key:        "insurance",
      label:      "Marine Cargo Insurance",
      pdfPath:    "/sign-docs/insurance-t3.pdf",
      issuerName: "Pampagro Importação Ltda",
      issuerOrg:  "Insured (Buyer arranges under FOB) · BR",
    },
    {
      key:        "bulk-cargo",
      label:      "Bulk Cargo Cert. of Quality",
      pdfPath:    "/sign-docs/bulk-cargo-cert-t3.pdf",
      issuerName: "Independent Surveyor",
      issuerOrg:  "Pre-shipment inspection · Abu Dhabi Port",
    },
    {
      key:        "phyto",
      label:      "Phytosanitary Cert.",
      pdfPath:    "/sign-docs/phytosanitary-cert-t3.pdf",
      issuerName: "UAE Ministry of Climate Change & Environment",
      issuerOrg:  "Plant-health authority · UAE",
    },
    {
      key:        "coo",
      label:      "Certificate of Origin",
      pdfPath:    "/sign-docs/certificate-of-origin-t3.pdf",
      issuerName: "Federation of UAE Chambers",
      issuerOrg:  "FUC · counter-attested by ABCC at destination",
    },
    {
      key:        "ebl",
      label:      "Electronic Bill of Lading",
      pdfPath:    "/sign-docs/ebl-t3.pdf",
      issuerName: "Demo Liner Co.",
      issuerOrg:  "Carrier · Abu Dhabi Port → Itaqui",
    },
  ],
};

const T4: Dossier = {
  tradeRef: "ORB-CTR-2026-0481",
  parties:  "Verde Carnes Brasil S.A. ↔ Al Bayt Premium Meats LLC",
  summary:  "CIP Viracopos → Dubai · USD 280K · 2,500 kg chilled halal beef tenderloin · ✈ Qatar Airways Cargo · 7-day FAB LC",
  docs: [
    {
      key:        "contract",
      label:      "Sale & Purchase Agreement",
      pdfPath:    "/sign-docs/contract-t4.pdf",
      issuerName: "Verde Carnes Brasil S.A. & Al Bayt Premium Meats LLC",
      issuerOrg:  "Bilateral · signed by both Corp DIDs",
    },
    {
      key:        "einvoice",
      label:      "Commercial E-Invoice",
      pdfPath:    "/sign-docs/einvoice-t4.pdf",
      issuerName: "Verde Carnes Brasil S.A.",
      issuerOrg:  "Seller (Issuer) · BR · PINT BR",
    },
    {
      key:        "insurance",
      label:      "Air Cargo Insurance",
      pdfPath:    "/sign-docs/insurance-t4.pdf",
      issuerName: "Verde Carnes Brasil S.A.",
      issuerOrg:  "Insured (Seller arranges under CIP) · BR",
    },
    {
      key:        "halal",
      label:      "Halal Slaughter Cert.",
      pdfPath:    "/sign-docs/halal-certificate-t4.pdf",
      issuerName: "CDIAL Halal",
      issuerOrg:  "Centro de Divulgação do Islam · BR",
    },
    {
      key:        "vet",
      label:      "Veterinary Health Cert.",
      pdfPath:    "/sign-docs/vet-certificate-t4.pdf",
      issuerName: "MAPA",
      issuerOrg:  "Ministério da Agricultura · BR · meat-specific",
    },
    {
      key:        "coo",
      label:      "Certificate of Origin",
      pdfPath:    "/sign-docs/certificate-of-origin-t4.pdf",
      issuerName: "ABCC",
      issuerOrg:  "Arab-Brazilian Chamber of Commerce · BR",
    },
    {
      key:        "awb",
      label:      "Air Waybill (AWB)",
      pdfPath:    "/sign-docs/awb-t4.pdf",
      issuerName: "Qatar Airways Cargo",
      issuerOrg:  "Carrier · Viracopos → Doha → Dubai · MAWB 157-VCP-DXB",
    },
  ],
};

const BY_SCENARIO: Record<string, Dossier> = {
  "t1-onboarding":        T1,
  "t2-bank-financed":     T2,
  "t3-reverse-corridor":  T3,
  "t4-air-cargo":         T4,
};

export function resolveDossier(scenarioId: string): Dossier {
  return BY_SCENARIO[scenarioId] ?? T1;
}
